import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Button, Pill } from "../../components/common/Primitives";
import {
  Loader2, ShieldCheck, Sparkles, TrendingUp, FileCheck2, AlertTriangle,
  ChevronRight, BadgeCheck, ClipboardCheck, Building2, User, Lock,
  FileText, Eye, EyeOff, Plus, ChevronDown, ChevronUp, Download, Share2,
} from "lucide-react";
import api, { formatApiError, API_BASE } from "../../lib/api";
import RejectionFeedbackCard from "../../components/RejectionFeedbackCard";
import InvestorDaaSStore from "../../components/marketplace/InvestorDaaSStore";
import PassportShareModal from "../../components/PassportShareModal";
import PassportShareAnalytics from "../../components/passport/PassportShareAnalytics";
import FounderVideoIntroCard from "../../components/startup/FounderVideoIntroCard";
import VerifiedBadge from "../../components/common/VerifiedBadge";

// Wrapper so the fetch-per-mount is scoped inside owner branch only.
function OwnerRejectionCard() { return <RejectionFeedbackCard testId="owner-rejection-feedback" />; }

const LABEL_TONE = {
  "Verified": "success",
  "Investor Ready": "success",
  "Almost Ready": "gold",
  "Needs Review": "warn",
  "High Risk": "danger",
  "Insufficient Data": "muted",
};

const CRIT_TONE = { Critical: "danger", Important: "warn", "Good to Have": "muted" };

function LadderRail({ ladder }) {
  const cur = ladder.current_level;
  return (
    <Card testId="passport-ladder" className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline">READINESS LADDER</div>
          <div className="font-heading text-ink text-2xl">Level {cur} / 10</div>
        </div>
        <Pill tone={cur >= 8 ? "success" : cur >= 5 ? "gold" : "muted"} testId="ladder-completion">
          {ladder.completion_pct}% complete
        </Pill>
      </div>
      <div className="grid grid-cols-10 gap-1 mb-3">
        {ladder.levels.map((l) => (
          <div
            key={l.level}
            title={`${l.title}${l.missing.length ? " · missing: " + l.missing.join(", ") : ""}`}
            className={`h-2.5 rounded-full ${l.passed ? "bg-emerald-500" : l.level === cur + 1 ? "bg-gold" : "bg-navy/10"}`}
            data-testid={`ladder-step-${l.level}`}
          />
        ))}
      </div>
      {ladder.next_step && (
        <div className="text-xs text-ink-soft border-t border-navy/[0.06] pt-3">
          <span className="font-heading text-ink">Next: {ladder.next_step.title}</span>
          {ladder.next_step.missing?.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {ladder.next_step.missing.map((m, i) => (
                <Pill key={i} tone="muted" testId={`next-missing-${i}`}>{m}</Pill>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function TrustCard({ trust, onOverride }) {
  const label = trust.label;
  return (
    <Card testId="passport-trust" className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline flex items-center gap-1"><TrendingUp size={12} /> VENTURAZ TRUST SCORE</div>
          <div className="font-display text-ink text-5xl mt-1">{trust.total}<span className="text-xl text-ink-muted">/100</span></div>
        </div>
        <Pill tone={LABEL_TONE[label] || "muted"} testId="trust-label">{label}</Pill>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {Object.entries(trust.components).map(([key, c]) => (
          <div
            key={key}
            className="rounded border border-navy/[0.08] px-3 py-2 flex items-center justify-between"
            data-testid={`trust-comp-${key}`}
          >
            <div>
              <div className="text-xs font-heading text-ink capitalize">{key.replace("_", " ")}</div>
              <div className="text-[10px] text-ink-muted">weight {trust.weights[key]}%</div>
            </div>
            <div className="flex items-center gap-2">
              <div className={`text-lg font-heading ${c.score >= 70 ? "text-emerald-600" : c.score >= 40 ? "text-amber-600" : "text-rose-600"}`}>
                {c.score}
              </div>
              {c.overridden && <Pill tone="gold" testId={`comp-overridden-${key}`}>OR</Pill>}
              {onOverride && (
                <Button variant="ghost" onClick={() => onOverride(key, c)} data-testid={`comp-override-${key}`}>Edit</Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function RecommendationsCard({ recs }) {
  if (!recs?.length) return null;
  const grouped = { Critical: [], Important: [], "Good to Have": [] };
  recs.forEach((r) => grouped[r.criticality]?.push(r));
  return (
    <Card testId="passport-recs" className="mb-6">
      <div className="overline mb-3 flex items-center gap-1"><Sparkles size={12} /> IMPROVEMENT RECOMMENDATIONS</div>
      {Object.entries(grouped).map(([crit, items]) => items.length ? (
        <div key={crit} className="mb-3">
          <div className="text-[10px] tracking-widest uppercase mb-2 flex items-center gap-2">
            <Pill tone={CRIT_TONE[crit]}>{crit}</Pill>
            <span className="text-ink-muted">{items.length}</span>
          </div>
          <div className="space-y-2">
            {items.map((r, i) => (
              <div key={i} className="rounded border border-navy/[0.06] px-3 py-2" data-testid={`rec-${crit.toLowerCase().replace(" ","-")}-${i}`}>
                <div className="text-sm font-heading text-ink">{r.action}</div>
                <div className="text-xs text-ink-soft mt-1">{r.why}</div>
                <div className="text-[10px] text-ink-muted mt-1 font-mono">Impact: {r.impact}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null)}
    </Card>
  );
}

function BadgeStrip({ badges }) {
  if (!badges?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="passport-badges">
      {badges.map((b, i) => (
        <Pill key={i} tone="gold" testId={`badge-${i}`}><BadgeCheck size={10} className="inline mr-1" /> {b}</Pill>
      ))}
    </div>
  );
}

function TemplatePicker({ current, options, canEdit, onChange }) {
  return (
    <Card testId="passport-template" className="mb-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline flex items-center gap-1"><ClipboardCheck size={12} /> DILIGENCE TEMPLATE</div>
          <div className="font-heading text-ink text-lg mt-1">{current?.label}</div>
        </div>
        {canEdit && options && (
          <select
            value={current?.key}
            onChange={(e) => onChange(e.target.value)}
            className="text-sm bg-white border border-navy/15 rounded px-3 py-2"
            data-testid="template-select"
          >
            {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        )}
      </div>
    </Card>
  );
}

export default function StartupPassport({ scope = "owner" }) {
  const nav = useNavigate();
  const { startupId: routeId } = useParams();
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(null); // {key, current}
  const [shareOpen, setShareOpen] = useState(false);
  const [watching, setWatching] = useState(false);
  const [watchBusy, setWatchBusy] = useState(false);

  const isAdmin = scope === "admin";
  const isOwner = scope === "owner";
  const isInvestor = scope === "investor";

  const endpoint =
    scope === "admin" ? `/admin/startup-passport/${routeId}`
    : scope === "investor" ? `/investor/startup-passport/${routeId}`
    : "/startup/passport";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setPassport(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [endpoint]);

  // Check watchlist status for investors viewing a startup passport.
  useEffect(() => {
    if (!isInvestor || !routeId) return;
    let cancel = false;
    api.get("/investor/watchlist")
      .then((r) => {
        if (cancel) return;
        const found = (r.data?.items || []).some((i) => i.startup_id === routeId);
        setWatching(found);
      })
      .catch(() => { /* silent */ });
    return () => { cancel = true; };
  }, [isInvestor, routeId]);

  const toggleWatch = async () => {
    if (!routeId) return;
    setWatchBusy(true);
    try {
      if (watching) {
        await api.delete(`/investor/watchlist/${routeId}`);
        setWatching(false);
        toast.success("Removed from watchlist");
      } else {
        await api.post(`/investor/watchlist/${routeId}`, {});
        setWatching(true);
        toast.success("Added to watchlist");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWatchBusy(false); }
  };

  const changeTemplate = async (key) => {
    setSaving(true);
    try {
      await api.post(`/admin/startups/${passport.startup.startup_id}/diligence-template`, { key });
      toast.success(`Template updated: ${key}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const applyOverride = async (key, score, note) => {
    setSaving(true);
    try {
      await api.post(`/admin/startups/${passport.startup.startup_id}/trust-score/override`, {
        component: key, score: Number(score), note,
      });
      toast.success("Component override saved");
      setOverrideOpen(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <DashboardShell title="Startup Passport" subtitle="LOADING">
        <div className="text-ink-muted"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>
      </DashboardShell>
    );
  }
  if (!passport) return null;

  const name = scope === "investor"
    ? passport.startup?.startup_name
    : (passport.startup?.startup_name || "Unnamed startup");

  const subtitle = scope === "admin" ? "ADMIN · VERIFIED STARTUP PASSPORT"
    : scope === "investor" ? "INVESTOR VIEW · CURATED"
    : "STARTUP · MY PASSPORT";

  return (
    <DashboardShell
      title={name}
      subtitle={subtitle}
      actions={
        scope === "admin" ? (
          <Button variant="ghost" onClick={() => nav(-1)} data-testid="passport-back">Back</Button>
        ) : isOwner ? (
          <Button onClick={() => setShareOpen(true)} data-testid="passport-owner-share-btn">
            <Share2 size={14} className="mr-1" /> Share Passport
          </Button>
        ) : isInvestor ? (
          <Button
            variant={watching ? "ghost" : "default"}
            disabled={watchBusy}
            onClick={toggleWatch}
            data-testid="passport-investor-watch-btn"
          >
            {watching ? "★ Watching" : "☆ Watch"}
          </Button>
        ) : null
      }
    >
      <Toaster richColors position="top-right" />
      {isOwner && (
        <PassportShareModal open={shareOpen} onClose={() => setShareOpen(false)} />
      )}
      {isOwner && <PassportShareAnalytics />}
      {isOwner && (
        <div className="mb-6">
          <FounderVideoIntroCard />
        </div>
      )}
      {(passport.startup?.has_logo || passport.startup?.logo_storage_path) && passport.startup?.startup_id && (
        <div className="flex items-center gap-4 mb-4" data-testid="passport-brand-header">
          <div className="w-16 h-16 rounded-lg border border-white/10 bg-ink-surface/60 flex items-center justify-center overflow-hidden shrink-0">
            <img
              src={`${API_BASE}/marketplace/startup/${passport.startup.startup_id}/logo`}
              alt=""
              className="max-w-full max-h-full object-contain"
              onError={(e) => { e.currentTarget.parentElement.parentElement.style.display = "none"; }}
            />
          </div>
          <div className="text-xs text-ink-muted font-mono uppercase tracking-widest">Brand identity</div>
        </div>
      )}
      <BadgeStrip badges={passport.badges} />
      {passport.trust_badge?.tier && (
        <div className="mb-4 -mt-2" data-testid="passport-verified-badge-wrap">
          <VerifiedBadge badge={passport.trust_badge} size="lg" />
        </div>
      )}

      {scope !== "investor" && (
        <div className="grid md:grid-cols-3 gap-3 mb-6">
          <MetaTile icon={Building2} label="Sector" value={passport.startup?.sector || "—"} testId="tile-sector" />
          <MetaTile icon={User} label="Stage" value={passport.startup?.business_stage || "—"} testId="tile-stage" />
          <MetaTile icon={TrendingUp} label="Funding ask" value={
            (passport.funding?.amount_required || passport.startup?.funding_ask)
              ? `₹${((passport.funding?.amount_required || passport.startup?.funding_ask) / 1e7).toFixed(2)} Cr` : "—"
          } testId="tile-ask" />
        </div>
      )}

      {scope === "investor" ? (
        <>
          <Card testId="investor-snapshot" className="mb-6">
            <div className="grid md:grid-cols-3 gap-4">
              <div><div className="overline">SECTOR / STAGE</div><div className="font-heading text-ink">{passport.startup?.sector} · {passport.startup?.business_stage}</div></div>
              <div><div className="overline">FUNDING ASK</div><div className="font-heading text-ink">{passport.startup?.funding_ask ? `₹${(passport.startup.funding_ask / 1e7).toFixed(2)} Cr` : "—"}</div></div>
              <div><div className="overline">REVENUE RANGE</div><div className="font-heading text-ink">{passport.startup?.revenue_range}</div></div>
            </div>
          </Card>
          <TrustSummary trust={passport.trust} />
          <Card testId="investor-locks" className="mb-6">
            <div className="overline mb-2 flex items-center gap-1"><Lock size={12} /> ACCESS LOCKS</div>
            <div className="grid md:grid-cols-2 gap-2 text-xs">
              {Object.entries(passport.locked || {}).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2" data-testid={`lock-${k}`}>
                  {v ? <Lock size={12} className="text-rose-600" /> : <BadgeCheck size={12} className="text-emerald-600" />}
                  <span className="text-ink-soft capitalize">{k.replace(/_/g, " ")}</span>
                  <span className={`ml-auto text-[10px] uppercase tracking-widest ${v ? "text-rose-600" : "text-emerald-600"}`}>{v ? "LOCKED" : "OPEN"}</span>
                </div>
              ))}
            </div>
          </Card>
          <InvestorDaaSStore
            startupId={passport.startup?.startup_id}
            startupName={passport.startup?.startup_name}
          />
        </>
      ) : (
        <>
          <TrustCard trust={passport.trust} onOverride={isAdmin ? (k, c) => setOverrideOpen({ key: k, current: c }) : null} />
          <LadderRail ladder={passport.ladder} />
          <TemplatePicker
            current={passport.template}
            options={passport.template_options}
            canEdit={isAdmin}
            onChange={changeTemplate}
          />
          {(isOwner || isAdmin) && <RecommendationsCard recs={passport.recommendations} />}
          {isOwner && <OwnerRejectionCard />}
          {isAdmin && <DiligenceWorkflowPanel startupId={passport.startup?.startup_id} />}
          {isAdmin && <InvestorMemoPanel startupId={passport.startup?.startup_id} />}
          <Card testId="passport-docs">
            <div className="overline mb-2 flex items-center gap-1"><FileCheck2 size={12} /> DOCUMENTS ({passport.documents?.length || 0})</div>
            {passport.documents?.length === 0
              ? <div className="text-sm text-ink-muted">No documents uploaded yet.</div>
              : (
                <div className="grid md:grid-cols-2 gap-2 text-xs">
                  {passport.documents?.slice(0, 20).map((d, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 border border-navy/[0.06] rounded" data-testid={`doc-${i}`}>
                      <span className="text-ink capitalize">{(d.kind || "unknown").replace(/_/g, " ")}</span>
                      <span className="text-ink-faint text-[10px] font-mono">{d.uploaded_at?.slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              )}
          </Card>
        </>
      )}

      {/* Override modal (admin) */}
      {overrideOpen && (
        <OverrideModal
          data={overrideOpen}
          saving={saving}
          onCancel={() => setOverrideOpen(null)}
          onSave={(score, note) => applyOverride(overrideOpen.key, score, note)}
        />
      )}
    </DashboardShell>
  );
}

function MetaTile({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded border border-navy/10 bg-white px-4 py-3" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-ink-muted"><Icon size={11} /> {label}</div>
      <div className="text-lg font-heading text-ink mt-1 truncate">{value}</div>
    </div>
  );
}

function TrustSummary({ trust }) {
  return (
    <Card testId="investor-trust" className="mb-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">TRUST SCORE</div>
          <div className="font-display text-ink text-4xl mt-1">{trust.total}<span className="text-lg text-ink-muted">/100</span></div>
        </div>
        <Pill tone={LABEL_TONE[trust.label] || "muted"} testId="investor-trust-label">{trust.label}</Pill>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4 text-[10px]">
        {Object.entries(trust.component_scores || {}).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between px-2 py-1 border border-navy/5 rounded">
            <span className="capitalize text-ink-soft">{k.replace("_", " ")}</span>
            <span className={`font-heading ${v >= 70 ? "text-emerald-600" : v >= 40 ? "text-amber-600" : "text-rose-600"}`}>{v}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function OverrideModal({ data, saving, onCancel, onSave }) {
  const [score, setScore] = useState(data.current.score);
  const [note, setNote] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel} data-testid="override-modal">
      <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="overline mb-2">TRUST SCORE OVERRIDE</div>
        <div className="font-heading text-ink text-lg mb-4 capitalize">{data.key.replace("_", " ")}</div>
        <label className="text-xs text-ink-soft">Score (0-100)</label>
        <input type="number" min="0" max="100" value={score} onChange={(e) => setScore(e.target.value)}
          className="mt-1 mb-3 w-full text-sm bg-white border border-navy/15 rounded px-3 py-2" data-testid="override-score" />
        <label className="text-xs text-ink-soft">Note (optional)</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
          className="mt-1 w-full text-sm bg-white border border-navy/15 rounded px-3 py-2" data-testid="override-note" />
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={() => onSave(score, note)} disabled={saving} data-testid="override-save">
            {saving ? <Loader2 className="animate-spin inline mr-1" size={12} /> : <ChevronRight size={12} className="inline mr-1" />}
            Save override
          </Button>
        </div>
      </div>
    </div>
  );
}


function InvestorMemoPanel({ startupId }) {
  const [memos, setMemos] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedInvestorId, setSelectedInvestorId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [visToggle, setVisToggle] = useState(null);

  const load = async () => {
    try {
      const [m, i] = await Promise.all([
        api.get(`/admin/investor-memos?startup_id=${startupId}&limit=20`),
        api.get(`/admin/investors?limit=50`),
      ]);
      setMemos(m.data.items || []);
      const invList = Array.isArray(i.data) ? i.data : (i.data.items || []);
      setInvestors(invList);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (startupId) load(); }, [startupId]);

  const generate = async () => {
    if (!selectedInvestorId) { toast.error("Pick an investor first"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/startups/${startupId}/investor-memo/${selectedInvestorId}`);
      toast.success(`Memo generated (${data.memo_id})`);
      setPickerOpen(false);
      setSelectedInvestorId("");
      setExpanded({ ...expanded, [data.memo_id]: true });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGenerating(false); }
  };

  const toggleVisibility = async (memo) => {
    setVisToggle(memo.memo_id);
    try {
      await api.patch(`/admin/investor-memos/${memo.memo_id}`, {
        visible_to_investor: !memo.visible_to_investor,
      });
      toast.success(memo.visible_to_investor ? "Hidden from investor" : "Shared with investor");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setVisToggle(null); }
  };

  const downloadMemoPdf = async (memo) => {
    try {
      const res = await api.get(`/admin/investor-memos/${memo.memo_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const safe = (memo.context?.startup_name || "startup").replace(/\s+/g, "_").replace(/[^\w.-]/g, "");
      a.download = `venturaz-memo-${safe}-${memo.memo_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Memo PDF downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const investorLabel = (id) => {
    const inv = investors.find((x) => x.investor_id === id || x.user_id === id);
    return inv?.display_name || inv?.email || id;
  };

  return (
    <Card testId="passport-memos" className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="overline flex items-center gap-1"><FileText size={12} /> INVESTOR MEMOS ({memos?.length || 0})</div>
        <Button variant="dark" onClick={() => setPickerOpen((v) => !v)} data-testid="memo-generate-toggle">
          <Plus size={12} className="inline mr-1" /> Generate memo
        </Button>
      </div>

      {pickerOpen && (
        <div className="mb-3 p-3 rounded border border-navy/10 bg-navy/[0.02]" data-testid="memo-picker">
          <div className="text-xs text-ink-soft mb-2">Pick an investor to tailor the memo to their mandate.</div>
          <div className="flex flex-wrap gap-2">
            <select
              value={selectedInvestorId}
              onChange={(e) => setSelectedInvestorId(e.target.value)}
              className="flex-1 min-w-[220px] text-sm bg-white border border-navy/15 rounded px-3 py-2"
              data-testid="memo-investor-select"
            >
              <option value="">Select investor…</option>
              {investors.map((inv) => (
                <option key={inv.investor_id || inv.user_id} value={inv.investor_id || inv.user_id}>
                  {inv.display_name || inv.email} {inv.firm_name ? `· ${inv.firm_name}` : ""}
                </option>
              ))}
            </select>
            <Button variant="primary" onClick={generate} disabled={generating || !selectedInvestorId} data-testid="memo-generate-btn">
              {generating ? <Loader2 className="animate-spin inline mr-1" size={12} /> : <Sparkles size={12} className="inline mr-1" />}
              Generate
            </Button>
            <Button variant="ghost" onClick={() => setPickerOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {memos === null ? (
        <div className="text-sm text-ink-muted"><Loader2 className="animate-spin inline mr-1" size={12} /> Loading…</div>
      ) : memos.length === 0 ? (
        <div className="text-sm text-ink-muted py-3">No memos generated yet. Generate one to preview and share with a specific investor.</div>
      ) : (
        <div className="space-y-2">
          {memos.map((memo) => {
            const isOpen = expanded[memo.memo_id];
            return (
              <div key={memo.memo_id} className="border border-navy/[0.08] rounded" data-testid={`memo-row-${memo.memo_id}`}>
                <div className="flex items-center gap-3 px-3 py-2">
                  <FileText size={14} className="text-navy shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-heading text-ink truncate">
                      For {investorLabel(memo.investor_id)}
                    </div>
                    <div className="text-[11px] text-ink-soft font-mono">
                      {memo.memo_id} · {memo.generated_at?.slice(0, 16).replace("T", " ")} · by {memo.generated_by}
                    </div>
                  </div>
                  <Pill tone={memo.visible_to_investor ? "success" : "muted"} testId={`memo-vis-${memo.memo_id}`}>
                    {memo.visible_to_investor ? "SHARED" : "PRIVATE"}
                  </Pill>
                  <Button
                    variant="ghost"
                    onClick={() => downloadMemoPdf(memo)}
                    data-testid={`memo-pdf-${memo.memo_id}`}
                    title="Download memo as PDF"
                  >
                    <Download size={12} />
                    <span className="ml-1 hidden sm:inline">PDF</span>
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => toggleVisibility(memo)}
                    disabled={visToggle === memo.memo_id}
                    data-testid={`memo-share-${memo.memo_id}`}
                  >
                    {visToggle === memo.memo_id ? <Loader2 className="animate-spin" size={12} />
                      : memo.visible_to_investor ? <EyeOff size={12} /> : <Eye size={12} />}
                    <span className="ml-1">{memo.visible_to_investor ? "Unshare" : "Share"}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setExpanded({ ...expanded, [memo.memo_id]: !isOpen })}
                    data-testid={`memo-toggle-${memo.memo_id}`}
                  >
                    {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </Button>
                </div>
                {isOpen && (
                  <div className="px-4 py-3 border-t border-navy/[0.06] bg-navy/[0.01]">
                    <pre className="whitespace-pre-wrap font-body text-xs text-ink leading-relaxed max-h-96 overflow-y-auto" data-testid={`memo-body-${memo.memo_id}`}>{memo.narrative}</pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function DiligenceWorkflowPanel({ startupId }) {
  const [data, setData] = useState(null);
  const [states, setStates] = useState([]);
  const [targetState, setTargetState] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    try {
      const [w, s] = await Promise.all([
        api.get(`/admin/diligence-workflow/${startupId}`),
        api.get(`/diligence-workflow/states`),
      ]);
      setData(w.data);
      setStates(s.data.states || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (startupId) load(); }, [startupId]);

  const move = async () => {
    if (!targetState) { toast.error("Pick a target state"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/diligence-workflow/${startupId}/transition`, {
        to_state: targetState, note: note || null,
      });
      toast.success("State transitioned");
      setTargetState(""); setNote("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!data) return (
    <Card testId="passport-workflow" className="mb-6">
      <div className="text-sm text-ink-muted"><Loader2 className="animate-spin inline mr-1" size={12} /> Loading workflow…</div>
    </Card>
  );

  const currentIdx = states.findIndex((s) => s.key === data.workflow.state);
  const history = data.workflow.history || [];

  return (
    <Card testId="passport-workflow" className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline flex items-center gap-1"><ClipboardCheck size={12} /> DILIGENCE WORKFLOW</div>
          <div className="font-heading text-ink text-xl mt-1" data-testid="workflow-state-label">{data.state_label}</div>
        </div>
        <Pill tone="gold" testId="workflow-state-order">Step {currentIdx + 1} / {states.length}</Pill>
      </div>

      {/* Progress rail */}
      <div className="flex gap-1 mb-4">
        {states.map((s) => (
          <div
            key={s.key}
            title={s.label}
            className={`h-2 flex-1 rounded ${s.order < currentIdx ? "bg-emerald-500" : s.order === currentIdx ? "bg-gold" : "bg-navy/10"}`}
            data-testid={`workflow-rail-${s.key}`}
          />
        ))}
      </div>

      {/* Transition controls */}
      <div className="flex gap-2 mb-3">
        <select
          value={targetState}
          onChange={(e) => setTargetState(e.target.value)}
          className="flex-1 text-sm bg-white border border-navy/15 rounded px-3 py-2"
          data-testid="workflow-target-select"
        >
          <option value="">Move to…</option>
          {states.map((s) => (
            <option key={s.key} value={s.key} disabled={s.key === data.workflow.state}>
              {s.order + 1}. {s.label}
            </option>
          ))}
        </select>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Internal note (optional)"
          className="flex-1 text-sm bg-white border border-navy/15 rounded px-3 py-2"
          data-testid="workflow-note"
        />
        <Button variant="primary" onClick={move} disabled={saving || !targetState} data-testid="workflow-transition-btn">
          {saving ? <Loader2 className="animate-spin inline mr-1" size={12} /> : <ChevronRight size={12} className="inline mr-1" />}
          Move
        </Button>
      </div>

      {history.length > 0 && (
        <div>
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="text-[11px] uppercase tracking-widest text-ink-muted hover:text-ink transition-colors flex items-center gap-1"
            data-testid="workflow-history-toggle"
          >
            {showHistory ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            History ({history.length})
          </button>
          {showHistory && (
            <div className="mt-3 space-y-2 max-h-64 overflow-y-auto" data-testid="workflow-history">
              {[...history].reverse().map((h, i) => (
                <div key={i} className="border-l-2 border-navy/10 pl-3 py-1" data-testid={`workflow-history-${i}`}>
                  <div className="text-xs font-heading text-ink">
                    {states.find((s) => s.key === h.state)?.label || h.state}
                  </div>
                  <div className="text-[11px] text-ink-soft font-mono">
                    {h.at?.slice(0, 16).replace("T", " ")} · {h.actor_email}
                    {h.from_state && ` · from ${states.find((s) => s.key === h.from_state)?.label || h.from_state}`}
                  </div>
                  {h.note && <div className="text-xs text-ink-soft italic mt-0.5">&ldquo;{h.note}&rdquo;</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

